import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Tag, Space, Button, Spin, message, Modal } from 'antd';
import { CalendarOutlined, UserOutlined, EyeOutlined, LikeOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import { mockPosts } from '../../api/mockData';
import './index.css';

const { confirm } = Modal;

const PostDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);

  useEffect(() => {
    loadPost();
  }, [id]);

  const loadPost = () => {
    setTimeout(() => {
      // 先从 localStorage 查找
      const savedPosts = JSON.parse(localStorage.getItem('blog_posts') || '[]');
      let foundPost = savedPosts.find(p => p.id === parseInt(id));
      
      // 如果没找到，从 mock 数据中找
      if (!foundPost) {
        foundPost = mockPosts.find(p => p.id === parseInt(id));
      }
      
      if (foundPost) {
        setPost(foundPost);
      }
      setLoading(false);
    }, 500);
  };

  const handleLike = () => {
    if (!liked) {
      setLiked(true);
      
      // 更新 localStorage 中的点赞数
      const savedPosts = JSON.parse(localStorage.getItem('blog_posts') || '[]');
      const index = savedPosts.findIndex(p => p.id === post.id);
      
      if (index !== -1) {
        savedPosts[index].likes += 1;
        localStorage.setItem('blog_posts', JSON.stringify(savedPosts));
        setPost({ ...post, likes: post.likes + 1 });
      }
      
      message.success('点赞成功！');
    } else {
      message.info('已经点过赞了');
    }
  };

  const handleEdit = () => {
    navigate(`/edit/${post.id}`);
  };

  const handleDelete = () => {
    confirm({
      title: '确认删除',
      content: '确定要删除这篇文章吗？删除后无法恢复。',
      okText: '确定删除',
      okType: 'danger',
      cancelText: '取消',
      onOk() {
        // 从 localStorage 删除
        const savedPosts = JSON.parse(localStorage.getItem('blog_posts') || '[]');
        const newPosts = savedPosts.filter(p => p.id !== post.id);
        localStorage.setItem('blog_posts', JSON.stringify(newPosts));
        
        message.success('文章删除成功！');
        setTimeout(() => {
          navigate('/');
        }, 1000);
      }
    });
  };

  const updateViewCount = () => {
    if (post) {
      const savedPosts = JSON.parse(localStorage.getItem('blog_posts') || '[]');
      const index = savedPosts.findIndex(p => p.id === post.id);
      
      if (index !== -1) {
        savedPosts[index].views += 1;
        localStorage.setItem('blog_posts', JSON.stringify(savedPosts));
        setPost({ ...post, views: post.views + 1 });
      }
    }
  };

  useEffect(() => {
    if (post) {
      updateViewCount();
    }
  }, [post]);

  if (loading) {
    return (
      <div className="post-detail-loading">
        <Spin size="large" tip="加载中..." />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="post-not-found">
        <h2>文章不存在</h2>
        <p>抱歉，您访问的文章不存在或已被删除。</p>
        <Button type="primary" onClick={() => navigate('/')}>
          返回首页
        </Button>
      </div>
    );
  }

  return (
    <div className="post-detail-page">
      <Card className="post-detail-card">
        <div className="post-header">
          <h1 className="post-title">{post.title}</h1>
          <div className="post-meta">
            <Space size="large">
              <span className="meta-item">
                <UserOutlined />
                <span>{post.author}</span>
              </span>
              <span className="meta-item">
                <CalendarOutlined />
                <span>{post.date}</span>
              </span>
              <span className="meta-item">
                <EyeOutlined />
                <span>{post.views}</span>
              </span>
              <span className="meta-item">
                <LikeOutlined />
                <span>{post.likes + (liked ? 1 : 0)}</span>
              </span>
            </Space>
          </div>
          <div className="post-tags">
            {post.tags.map(tag => (
              <Tag key={tag} color="blue">{tag}</Tag>
            ))}
          </div>
        </div>
        
        <div className="post-summary-section">
          <h3>文章摘要</h3>
          <p className="post-summary">{post.summary}</p>
        </div>
        
        <div className="post-content">
          <ReactMarkdown>{post.content}</ReactMarkdown>
        </div>
        
        <div className="post-actions">
          <Space>
            <Button 
              onClick={() => navigate(-1)} // 返回上一页
               >
              返回
            </Button>
            <Button 
              type="primary" 
              icon={<LikeOutlined />} 
              onClick={handleLike}
              disabled={liked}
            >
              {liked ? '已点赞' : '点赞'}
            </Button>
            <Button 
              type="default" 
              icon={<EditOutlined />}
              onClick={handleEdit}
            >
              编辑
            </Button>
            <Button 
              danger 
              icon={<DeleteOutlined />}
              onClick={handleDelete}
            >
              删除
            </Button>
          </Space>
        </div>
      </Card>
    </div>
  );
};

export default PostDetail;